import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
	addRoleService,
	applyLeaveService,
	deleteMyRequestedLeaveService,
	getEmployeeLeaveService,
	getEmployeeRequestedLeaveService,
	getLeaveBalanceService,
	getLeaveService,
	getRequestedLeaveService,
	inactiveRoleService,
	updateRequestedLeaveService,
	updateRoleService,
} from '../../../services/services';

export const addRole = createAsyncThunk('addRole', async (credentials, thunkAPI) => {
	try {
		const response = await addRoleService(credentials);
		return response.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

export const getRequestedLeaves = createAsyncThunk(
	'getRequestedLeaves',
	async (params, thunkAPI) => {
		try {
			const response = await getRequestedLeaveService(params);
			return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	},
);

export const getLeaves = createAsyncThunk('getEmployeeLeaves', async (data, thunkAPI) => {
	try {
		const response = await getEmployeeLeaveService(data?.id);

		return response.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

export const getMyRequestedLeaves = createAsyncThunk(
	'getMyRequestedLeaves',
	async (data, thunkAPI) => {
		try {
			const response = await getEmployeeRequestedLeaveService(data);

			return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	},
);

export const getLeavesBalance = createAsyncThunk('getLeavesBalance', async (data, thunkAPI) => {
	try {
		const response = await getLeaveBalanceService(data);
		return response.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

export const deleteMyRequestedLeaves = createAsyncThunk(
	'deleteMyRequestedLeaves',
	async (credentials, thunkAPI) => {
		try {
			const { id } = credentials;
			const response = await deleteMyRequestedLeaveService(id);

			return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	},
);

export const applyLeaves = createAsyncThunk('applyLeaves', async (credentials, thunkAPI) => {
	try {
		const response = await applyLeaveService(credentials);

		return response.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

export const updateLeave = createAsyncThunk('updateLeave', async (credentials, thunkAPI) => {
	try {
		const { id, ...reset } = credentials;

		const response = await updateRequestedLeaveService(id, reset);

		return response.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

export const updateRoles = createAsyncThunk('updateRoles', async (credentials, thunkAPI) => {
	try {
		const { id, ...reset } = credentials;

		const response = await updateRoleService(id, reset);

		return response.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

export const inactiveRoles = createAsyncThunk('inactiveRoles', async (credentials, thunkAPI) => {
	try {
		const { id, ...reset } = credentials;
		const response = await inactiveRoleService(id, reset);

		return response?.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

export const fetchLeaveTypes = createAsyncThunk('getLeaves', async (credentials, thunkAPI) => {
	try {
		const response = await getLeaveService(credentials);
		return response?.data;
	} catch (error) {
		return thunkAPI.rejectWithValue(error);
	}
});

const leaveSlice = createSlice({
	name: 'leaveSlice',
	initialState: {
		leaves: null,
		isLoading: false,
		error: null,
		requestedLeaves: null,
		isSubmitting: false,
		addingRoleError: null,
		roles: null,
		update: null,
		isUpdating: false,
		updateError: null,
		inAtiveRole: null,
		isInactive: false,
		updateLeave: null,
		applyLeave: null,
		isApply: false,
		applyError: null,
		myRequestedLeaves: {
			data: null,
			error: null,
			loading: false,
		},
		deleteMyRequestedLeaves: {
			data: null,
			error: null,
			loading: false,
		},
		leavesBalance: {
			data: null,
			error: null,
			loading: false,
		},
		leaveTypes: {
			data: null,
			error: null,
			loading: null,
		},
	},
	reducers: {
		resetLeaves: (state) => {
			state.leaves = null;
			state.isSubmitting = false;
			state.addingRoleError = null;
			// state.isLoading = false;
			state.error = null;
			state.update = null;
			state.isUpdating = null;
			state.updateError = null;
		},
		resetRequestedData: (state) => {
			state.applyLeave = null;
			// state.isLoading = false;
			state.applyError = null;
		},
		resetAction: (state) => {
			state.updateLeave = null;
			// state.isLoading = false;
			state.error = null;
		},
		resetMyRequestedLeaves: (state) => {
			state.myRequestedLeaves.data = null;
			state.myRequestedLeaves.loading = false;
			state.myRequestedLeaves.error = null;
		},
		resetDeleteMyRequestedLeave: (state) => {
			state.deleteMyRequestedLeaves.data = null;
			state.deleteMyRequestedLeaves.loading = false;
			state.deleteMyRequestedLeaves.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(addRole.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(addRole.fulfilled, (state, action) => {
				state.isLoading = false;
				state.leaves = action.payload;
				state.error = null;
			})
			.addCase(addRole.rejected, (state, action) => {
				state.isLoading = false;
				state.leaves = null;
				state.error = action.payload;
			});
		builder
			.addCase(updateRoles.pending, (state) => {
				state.isUpdating = true;
				state.updateError = null;
			})
			.addCase(updateRoles.fulfilled, (state, action) => {
				state.isUpdating = false;
				state.update = action.payload;
				state.updateError = null;
			})
			.addCase(updateRoles.rejected, (state, action) => {
				state.isUpdating = false;
				state.update = null;
				state.updateError = action.payload;
			});
		builder
			.addCase(getLeaves.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(getLeaves.fulfilled, (state, action) => {
				state.isLoading = false;
				state.leaves = action.payload;
				state.error = null;
			})
			.addCase(getLeaves.rejected, (state, action) => {
				state.isLoading = false;
				state.leaves = null;
				state.error = action.payload;
			});
		builder
			.addCase(getRequestedLeaves.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(getRequestedLeaves.fulfilled, (state, action) => {
				state.isLoading = false;
				state.requestedLeaves = action.payload;
				state.error = null;
			})
			.addCase(getRequestedLeaves.rejected, (state, action) => {
				state.isLoading = false;
				state.requestedLeaves = null;
				state.error = action.payload;
			});

		builder
			.addCase(applyLeaves.pending, (state) => {
				state.isLoading = true;
				state.applyError = null;
			})
			.addCase(applyLeaves.fulfilled, (state, action) => {
				state.isLoading = false;
				state.applyLeave = action.payload;
				state.applyError = null;
			})
			.addCase(applyLeaves.rejected, (state, action) => {
				state.isLoading = false;
				state.applyLeave = null;
				state.applyError = action.payload;
			});

		builder
			.addCase(updateLeave.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(updateLeave.fulfilled, (state, action) => {
				state.isLoading = false;
				state.updateLeave = action.payload;
				state.error = null;
			})
			.addCase(updateLeave.rejected, (state, action) => {
				state.isLoading = false;
				state.updateLeave = null;
				state.error = action.payload;
			});

		builder
			.addCase(inactiveRoles.pending, (state) => {
				state.isInactive = true;
				state.error = null;
			})
			.addCase(inactiveRoles.fulfilled, (state, action) => {
				state.isInactive = false;
				state.inAtiveRole = action.payload;
				state.error = null;
			})
			.addCase(inactiveRoles.rejected, (state, action) => {
				state.isInactive = false;
				state.inAtiveRole = null;
				state.error = action.payload;
			})
			.addCase(getMyRequestedLeaves.pending, (state) => {
				state.myRequestedLeaves.loading = true;
				state.myRequestedLeaves.error = null;
			})
			.addCase(getMyRequestedLeaves.fulfilled, (state, action) => {
				state.myRequestedLeaves.loading = false;
				state.myRequestedLeaves.data = action.payload.data;
				state.myRequestedLeaves.error = null;
			})
			.addCase(getMyRequestedLeaves.rejected, (state, action) => {
				state.myRequestedLeaves.loading = false;
				state.myRequestedLeaves.error = action.payload;
			})

			.addCase(deleteMyRequestedLeaves.pending, (state) => {
				state.deleteMyRequestedLeaves.loading = true;
				state.deleteMyRequestedLeaves.error = null;
			})
			.addCase(deleteMyRequestedLeaves.fulfilled, (state, action) => {
				state.deleteMyRequestedLeaves.loading = false;
				state.deleteMyRequestedLeaves.data = action.payload;
				state.deleteMyRequestedLeaves.error = null;
			})
			.addCase(deleteMyRequestedLeaves.rejected, (state, action) => {
				state.deleteMyRequestedLeaves.loading = false;
				state.deleteMyRequestedLeaves.error = action.payload;
			});

		builder
			.addCase(getLeavesBalance.pending, (state) => {
				state.leavesBalance.loading = true;
				state.leavesBalance.error = null;
			})
			.addCase(getLeavesBalance.fulfilled, (state, action) => {
				state.leavesBalance.loading = false;
				state.leavesBalance.data = action.payload.data;
				state.leavesBalance.error = null;
			})
			.addCase(getLeavesBalance.rejected, (state, action) => {
				state.leavesBalance.loading = false;
				state.leavesBalance.error = action.payload;
			});

		builder
			.addCase(fetchLeaveTypes.pending, (state) => {
				state.leaveTypes.loading = true;
				state.leaveTypes.error = null;
			})
			.addCase(fetchLeaveTypes.fulfilled, (state, action) => {
				state.leaveTypes.loading = false;
				state.leaveTypes.data = action.payload.data;
				state.leaveTypes.error = null;
			})
			.addCase(fetchLeaveTypes.rejected, (state, action) => {
				state.leaveTypes.loading = false;
				state.leaveTypes.error = action.payload;
			});
	},
});

export const {
	resetLeaves,
	resetRequestedData,
	resetMyRequestedLeaves,
	resetDeleteMyRequestedLeave,
	resetAction,
} = leaveSlice.actions;
export default leaveSlice.reducer;
